import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getHomeTemperatureThreshold,
  setHomeTemperatureThreshold,
  getAmbientTemperature,
  ambientTone,
  DEFAULT_HOME_TEMPERATURE_THRESHOLD,
  type HomeTemperatureThreshold,
} from "../../services/homeTemperatureThreshold";
import { formatTemp, type TempUnit } from "../../services/cookingState";
import HomeTempThresholdEditor, {
  isAmbientBandValid,
} from "./HomeTempThresholdEditor";
import Button from "./Button";
import IconButton from "./IconButton";
import EditIcon from "@mui/icons-material/Edit";
import ThermostatIcon from "@mui/icons-material/Thermostat";

type Props = {
  sensorName: string;
  unit: TempUnit;
};

function serialize(t: HomeTemperatureThreshold): string {
  return JSON.stringify(t);
}

/**
 * One Recent-Activity row that surfaces the current home (room) temperature
 * plus the configured comfort range, with an inline editor toggled by the
 * pencil button. Replaces the standalone SensorHomeTempCard so the threshold
 * controls sit next to the other "what's happening in this resident's home"
 * signals (last cooking session, last presence, last alert).
 */
/**
 * `measuredAt` is documented as an ISO string in UTC, and if it carries a Z this
 * is just `toLocaleString()`. But cts_app_web#62 is open precisely because
 * nobody has established what the API sends, and the sibling path (alarms_v2
 * `datetime`) is space-separated with no zone.
 *
 * `new Date("2026-09-16 12:19:21")` is NOT specified by ECMAScript: V8 accepts
 * it, JavaScriptCore historically returns Invalid Date. So on Safari this line
 * would render the literal string "Invalid Date" to an operator. Everywhere else
 * the ambiguity costs a silent offset; here it costs visible garbage, which
 * makes it worth handling before the question is settled.
 *
 * Normalising the separator does NOT resolve the zone -- a space-separated value
 * still parses as local, which is exactly what #62 tracks. It only guarantees we
 * never show garbage.
 */
function formatMeasuredAt(raw: string): string {
  const normalised = raw.includes("T") ? raw : raw.replace(" ", "T");
  const d = new Date(normalised);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
}

export default function SensorHomeTempActivityRow({ sensorName, unit }: Props) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<HomeTemperatureThreshold | null>(null);
  const [baseline, setBaseline] = useState<string>("");
  const [saved, setSaved] = useState(false);

  const { data: threshold, isPending: thresholdPending } = useQuery({
    queryKey: ["homeTemperatureThreshold", sensorName],
    queryFn: async () => {
      const result = await getHomeTemperatureThreshold(sensorName);
      return result ?? DEFAULT_HOME_TEMPERATURE_THRESHOLD;
    },
    enabled: !!sensorName,
    staleTime: 30_000,
    retry: false,
  });

  const { data: reading } = useQuery({
    queryKey: ["ambient-temperature", sensorName],
    queryFn: async () => await getAmbientTemperature(sensorName),
    enabled: !!sensorName,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (!threshold) return;
    const next = serialize(threshold);
    if (next !== baseline) {
      setBaseline(next);
      setDraft(threshold);
    }
  }, [threshold, baseline]);

  const save = useMutation({
    mutationFn: async (t: HomeTemperatureThreshold) =>
      await setHomeTemperatureThreshold(sensorName, t),
    onSuccess: () => {
      setSaved(true);
      setEditing(false);
      queryClient.invalidateQueries({
        queryKey: ["homeTemperatureThreshold", sensorName],
      });
    },
  });

  const dirty = draft != null && serialize(draft) !== baseline;
  // The shared rule, not a fourth copy of it. This was the last entry point
  // still carrying `draft.enabled &&`, which is the suppression removed
  // everywhere else: with the band toggled OFF, a zero-width range showed no
  // warning, Save stayed enabled, and it stored -- then went live the day
  // someone enabled it. Validity does not depend on whether the band is
  // currently on; see isAmbientBandValid.
  const invalid = draft != null && !isAmbientBandValid(draft.minC, draft.maxC);

  const onCancel = () => {
    if (baseline) setDraft(JSON.parse(baseline));
    setEditing(false);
    save.reset();
  };

  // Headline value: the current reading if we have one, otherwise an em dash.
  const value = reading ? formatTemp(reading.temperatureC, unit) : "—";

  // Tone of the headline temperature against the configured range.
  const tone = ambientTone(reading ?? null, threshold ?? null);
  const valueClass =
    tone === "hot"
      ? "text-alert-critical"
      : tone === "cold"
        ? "text-alert-info"
        : "text-ink";

  // Sub-line describing the configured range, or that alerts are off.
  let sub: string;
  if (!threshold || thresholdPending) {
    sub = "Loading range…";
  } else if (!threshold.enabled) {
    sub = "Range alerts off";
  } else {
    sub = `Range ${formatTemp(threshold.minC, unit)} – ${formatTemp(threshold.maxC, unit)}`;
  }

  return (
    <div className="py-3 border-b border-black/5 last:border-0">
      <div className="flex items-center gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-alert-info/10 text-alert-info">
          <ThermostatIcon sx={{ fontSize: 20 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wide text-ink-3">
            Home temperature
          </div>
          <div
            className={`text-sm font-medium truncate ${valueClass}`}
            title={
              reading
                ? `Reported ${formatMeasuredAt(reading.measuredAt)}`
                : "No reading yet"
            }
          >
            {value}
          </div>
          <div className="text-xs text-ink-3 truncate">{sub}</div>
        </div>
        {!editing && (
          <IconButton
            label="Edit home temperature range"
            onClick={() => {
              setSaved(false);
              setEditing(true);
            }}
            className="!w-8 !h-8 !bg-black/[0.04] !text-ink !border-black/10 hover:!bg-black/10"
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </div>

      {editing && draft && (
        <div className="mt-3 pt-3 border-t border-black/5">
          <HomeTempThresholdEditor
            value={draft}
            onChange={(next) => {
              setSaved(false);
              setDraft(next);
            }}
            unit={unit}
            disabled={save.isPending}
          />
          <div className="flex items-center justify-end gap-3 mt-4">
            {save.isPending && (
              <span className="text-ink-3 text-xs">Sending to device…</span>
            )}
            {save.isError && !save.isPending && (
              <span className="text-alert-critical text-xs">
                Couldn't reach the device. Try again.
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={save.isPending}
              className="!text-ink !border-black/15 hover:!bg-black/[0.04] disabled:!opacity-40"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => draft && save.mutate(draft)}
              disabled={!dirty || save.isPending || invalid}
            >
              Save Range
            </Button>
          </div>
        </div>
      )}

      {saved && !editing && (
        <div className="text-xs text-alert-success mt-2">Saved.</div>
      )}
    </div>
  );
}
